import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, FileDown, Github, Linkedin, Mail, MapPin } from "lucide-react";

function useTyping(words, speed = 80, pause = 1400) {
    const [text, setText] = useState("");
    const [i, setI] = useState(0);
    const [del, setDel] = useState(false);
    useEffect(() => {
        if (!words?.length) return;
        const word = words[i % words.length];
        let t;
        if (!del && text === word) t = setTimeout(() => setDel(true), pause);
        else if (del && text === "") { setDel(false); setI((x) => x + 1); }
        else t = setTimeout(() => setText(del ? word.slice(0, text.length - 1) : word.slice(0, text.length + 1)), del ? speed / 2 : speed);
        return () => clearTimeout(t);
    }, [text, del, i, words, speed, pause]);
    return text;
}

const line = { hidden: { y: "110%" }, show: (d) => ({ y: 0, transition: { duration: 0.9, delay: d, ease: [0.22, 1, 0.36, 1] } }) };

export default function Hero({ config }) {
    const roles = config?.roles || ["Software Engineer", "QA Engineer", "AI & Full Stack Developer"];
    const typed = useTyping(config?.typing || ["Building AI Products"]);
    const { scrollY } = useScroll();
    const y = useTransform(scrollY, [0, 500], [0, 120]);
    const opacity = useTransform(scrollY, [0, 400], [1, 0]);

    const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

    return (
        <section className="relative min-h-screen flex flex-col justify-center max-w-6xl mx-auto px-5 pt-28 pb-16">
            <motion.div style={{ y, opacity }}>
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                    className="inline-flex items-center gap-2 glass rounded-full pl-1.5 pr-3 py-1.5 mb-8 text-xs">
                    <span className="relative flex h-2 w-2 ml-1">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                    </span>
                    <span className="text-muted-foreground">{config?.availability || "Available for work"}</span>
                </motion.div>

                <h1 className="font-display font-semibold leading-[0.95] text-[13vw] sm:text-7xl lg:text-8xl">
                    {["Hariom", "Patel"].map((w, idx) => (
                        <span key={w} className="block overflow-hidden">
                            <motion.span custom={0.4 + idx * 0.12} variants={line} initial="hidden" animate="show" className="block">
                                {idx === 1 ? <span className="aurora-text">{w}</span> : w}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <div className="overflow-hidden mt-5">
                    <motion.p custom={0.7} variants={line} initial="hidden" animate="show"
                        className="font-display text-xl sm:text-2xl lg:text-3xl text-muted-foreground font-light">
                        {roles.join("  ·  ")}
                    </motion.p>
                </div>

                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
                    className="mt-6 font-mono text-sm sm:text-base text-foreground/80 h-6">
                    <span className="text-primary">{">"} </span>{typed}
                    <span className="inline-block w-2 h-4 bg-primary ml-0.5 animate-pulse align-middle" />
                </motion.div>

                <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }}
                    className="mt-8 max-w-xl text-muted-foreground leading-relaxed">
                    {config?.tagline}
                </motion.p>

                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25 }}
                    className="mt-10 flex flex-wrap items-center gap-3">
                    <button data-testid="hero-view-projects" onClick={() => scrollTo("#projects")}
                        className="group inline-flex items-center gap-2 bg-foreground text-background rounded-full px-6 py-3 text-sm font-medium hover:gap-3 transition-all hover:shadow-[0_0_30px_-5px] hover:shadow-primary/50">
                        View Projects <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                    </button>
                    <a data-testid="hero-resume" href={config?.resume_url || "/Resume.pdf"} target="_blank" rel="noreferrer" download="Hariom_Patel_Resume.pdf"
                        className="inline-flex items-center gap-2 glass rounded-full px-6 py-3 text-sm font-medium hover:text-primary transition-colors">
                        <FileDown className="w-4 h-4" /> Résumé
                    </a>
                    {[
                        { icon: Github, url: config?.socials?.github || "https://github.com/hariompatel61", id: "hero-github" },
                        { icon: Linkedin, url: config?.socials?.linkedin || "https://www.linkedin.com/in/hariompatel069/", id: "hero-linkedin" },
                    ].map((s) => (
                        <a key={s.id} data-testid={s.id} href={s.url || "#"} target="_blank" rel="noreferrer"
                            className="w-11 h-11 grid place-items-center glass rounded-full hover:text-primary hover:-translate-y-0.5 transition-all">
                            <s.icon className="w-4 h-4" />
                        </a>
                    ))}
                    <button data-testid="hero-contact" onClick={() => scrollTo("#contact")}
                        className="w-11 h-11 grid place-items-center glass rounded-full hover:text-primary hover:-translate-y-0.5 transition-all">
                        <Mail className="w-4 h-4" />
                    </button>
                </motion.div>

                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
                    className="mt-8 flex items-center gap-2 text-xs text-muted-foreground font-mono">
                    <MapPin className="w-3.5 h-3.5" /> {config?.location || "Remote"}
                </motion.div>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-muted-foreground">
                <span className="font-mono text-[10px] uppercase tracking-widest">Scroll</span>
                <div className="w-px h-10 bg-gradient-to-b from-primary to-transparent" />
            </motion.div>
        </section>
    );
}
